import React, { useState, useEffect } from 'react';
import './PendingAccounts.css';

const PendingAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [accountsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState({});

  const BASE_URL = 'https://server.tradefxonline.com';

  // Fetch pending accounts from API
  const fetchPendingAccounts = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${BASE_URL}/admin_master_pending.php`);
      const result = await response.json();
      
      if (result.status === 'success') {
        setAccounts(result.data || []);
      } else {
        setError('Failed to fetch pending accounts');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error('Error fetching pending accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingAccounts();
  }, []);

  // Handle approve account
  const handleApproveAccount = async (account) => {
    setActionLoading(prev => ({ ...prev, [account.trade_id]: 'approving' }));
    try {
      const response = await fetch(`${BASE_URL}/admin_approve_master.php?tradeid=${account.trade_id}`);
      const result = await response.json();
      
      if (result.status === 'success') {
        // Remove the approved account from the list
        setAccounts(prev => prev.filter(acc => acc.trade_id !== account.trade_id));
        alert('Account approved successfully!');
      } else {
        alert('Failed to approve account');
      }
    } catch (err) {
      alert('Error approving account');
      console.error('Error approving account:', err);
    } finally {
      setActionLoading(prev => ({ ...prev, [account.trade_id]: null }));
    }
  };

  // Handle cancel account
  const handleCancelAccount = async (account) => {
    if (!window.confirm('Are you sure you want to cancel this account?')) {
      return;
    }
    
    setActionLoading(prev => ({ ...prev, [account.trade_id]: 'canceling' }));
    try {
      const response = await fetch(`${BASE_URL}/admin_cancel_master.php?tradeid=${account.trade_id}`);
      const result = await response.json();
      
      if (result.status === 'success') {
        // Remove the canceled account from the list
        setAccounts(prev => prev.filter(acc => acc.trade_id !== account.trade_id));
        alert('Account canceled successfully!');
      } else {
        alert('Failed to cancel account');
      }
    } catch (err) {
      alert('Error canceling account');
      console.error('Error canceling account:', err);
    } finally {
      setActionLoading(prev => ({ ...prev, [account.trade_id]: null }));
    }
  };

  // Filter accounts based on search term
  const filteredAccounts = accounts.filter(account =>
    account.strategyname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    account.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    account.trade_id?.includes(searchTerm)
  );

  // Pagination logic
  const indexOfLastAccount = currentPage * accountsPerPage;
  const indexOfFirstAccount = indexOfLastAccount - accountsPerPage;
  const currentAccounts = filteredAccounts.slice(indexOfFirstAccount, indexOfLastAccount);
  const totalPages = Math.ceil(filteredAccounts.length / accountsPerPage);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getStrategyImage = (imagePath) => {
    if (!imagePath || imagePath === 'uploads/trend_rider.png') {
      return '/image.png'; // Default image
    }
    return `${BASE_URL}/${imagePath}`;
  };

  return (
    <div className="pending-accounts-container">
      <div className="page-header">
        <h1 className="page-title">Pending Accounts</h1>
        <div className="header-actions">
          <button 
            className="refresh-btn"
            onClick={fetchPendingAccounts}
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
          <div className="search-container">
            <input
              type="text"
              placeholder="Search by strategy, email, or trade ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <span>{error}</span>
          <button onClick={fetchPendingAccounts} className="retry-btn">
            Retry
          </button>
        </div>
      )}

      <div className="accounts-table-container">
        <table className="accounts-table">
          <thead>
            <tr>
              <th>Strategy Image</th>
              <th>Strategy Name</th>
              <th>Strategy About</th>
              <th>Email</th>
              <th>Trade ID</th>
              <th>Account Type</th>
              <th>Commission</th>
              <th>Min Deposit</th>
              <th>Registered Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="11" className="loading-cell">
                  <div className="loading-spinner">
                    Loading pending accounts...
                  </div>
                </td>
              </tr>
            ) : currentAccounts.length === 0 ? (
              <tr>
                <td colSpan="11" className="empty-cell">
                  {searchTerm ? 'No accounts found matching your search.' : 'No pending accounts found.'}
                </td>
              </tr>
            ) : (
              currentAccounts.map((account) => (
                <tr key={account.id}>
                  <td>
                    <div className="strategy-image-container">
                      <img
                        src={getStrategyImage(account.strategyimage)}
                        alt={account.strategyname || 'Strategy'}
                        className="strategy-image"
                        onError={(e) => {
                          e.target.src = '/image.png';
                        }}
                      />
                    </div>
                  </td>
                  <td>
                    <div className="strategy-name">
                      {account.strategyname || 'N/A'}
                    </div>
                  </td>
                  <td>
                    <div className="strategy-about" title={account.strategyabout}>
                      {account.strategyabout || 'N/A'}
                    </div>
                  </td>
                  <td>
                    <div className="email-cell">
                      {account.email}
                    </div>
                  </td>
                  <td>
                    <div className="trade-id">
                      {account.trade_id}
                    </div>
                  </td>
                  <td>
                    <div className="account-type">
                      {account.account_name || account.account_type || 'N/A'}
                    </div>
                  </td>
                  <td>
                    <div className="commission">
                      {account.commission || 'N/A'}
                    </div>
                  </td>
                  <td>
                    <div className="min-deposit">
                      ${account.mindeposit || '0'}
                    </div>
                  </td>
                  <td>
                    <div className="registered-date">
                      {formatDate(account.Registered_Date)}
                    </div>
                  </td>
                  <td>
                    <span className="status-badge pending">
                      Pending
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="action-btn approve-btn"
                        onClick={() => handleApproveAccount(account)}
                        disabled={actionLoading[account.trade_id]}
                      >
                        {actionLoading[account.trade_id] === 'approving' ? 'Approving...' : 'Approve'}
                      </button>
                      <button
                        className="action-btn cancel-btn"
                        onClick={() => handleCancelAccount(account)}
                        disabled={actionLoading[account.trade_id]}
                      >
                        {actionLoading[account.trade_id] === 'canceling' ? 'Canceling...' : 'Cancel'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!loading && filteredAccounts.length > 0 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {indexOfFirstAccount + 1} to {Math.min(indexOfLastAccount, filteredAccounts.length)} of {filteredAccounts.length} entries
          </div>
          <div className="pagination-controls">
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            {[...Array(totalPages)].map((_, index) => (
              <button
                key={index + 1}
                className={`pagination-btn ${currentPage === index + 1 ? 'active' : ''}`}
                onClick={() => setCurrentPage(index + 1)}
              >
                {index + 1}
              </button>
            ))}
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingAccounts;